import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import clsx from "clsx";
import Datetimepicker from "../Datetimepicker/Datetimepicker";
import InputField from "../InputField/InputField";
import SelectField from "../SelectField/SelectField";
import Toggle from "../Toggle/Toggle";
import RadioButton from "../RadioButton/RadioButton";
import type { RadioButtonProps } from "../RadioButton/RadioButton";
import Upload from "../Upload/Upload";
import Checkbox from "../Checkbox/Checkbox";
import SmartImage from "../SmartImage/SmartImage";
import type { SmartImageProps } from "../SmartImage/SmartImage";
import InputWysiwyg from "../InputWysiwyg/InputWysiwyg";
import type { InputWysiwygProps } from "../InputWysiwyg/InputWysiwyg";
import type { UploadFileWithPath } from "../Upload/Upload";
import type { SelectFieldProps } from "../SelectField/SelectField";
import type { ToggleProps } from "../Toggle/Toggle";
import type { UploadProps } from "../Upload/Upload";
import type { DatetimepickerProps } from "../Datetimepicker/Datetimepicker";
import type { CheckboxProps } from "../Checkbox/Checkbox";
import ColorPicker from "../ColorPicker/ColorPicker";
import { LicensePlateInput } from "../LicensePlate/LicensePlate";
import { normalizeOptions as __normalizeOptionsImported } from "./Form.utils";
import type { CodeFieldValue } from "../CodeField/CodeField";

// Wrapper seguro (fallback se não for função)
const normalizeOptionsSafe = async (
  options:
    | ReadonlyArray<{ label: string; value: string; disabled?: boolean }>
    | undefined,
  field: FormField,
  formData: Record<string, unknown>
): Promise<Array<{ label: string; value: string; disabled?: boolean }>> => {
  if (!options) return [];
  try {
    if (typeof __normalizeOptionsImported === "function") {
      const normalized = await __normalizeOptionsImported(
        options,
        field,
        formData
      );
      return [...normalized];
    }
  } catch {
    /* ignore */
  }
  return [...options];
};

// 1. Defina interfaces para cada tipo de campo
export interface FormField {
  id: string;
  type:
    | "input"
    | "select"
    | "toggle"
    | "radiobutton"
    | "upload"
    | "date"
    | "time"
    | "datetime"
    | "checkbox"
    | "licenseplate"
    | "smartimage"
    | "wysiwyg"
    | "colorpicker"
    | "code"; // novo tipo
  label?: string;
  required?: boolean;
  disabled?: boolean;
  visibleIf?: (formData: Record<string, unknown>) => boolean;
  colSpan?: number;
  rowSpan?: number;
  errorMessage?: string | ((context: unknown) => string);
  successMessage?: string | ((context: unknown) => string);
  successIcon?: React.ReactNode;
  helperText?: string;
  validators?: import("./Form").FieldValidator[];
  inputType?: string;
  options?: { label: string; value: string; disabled?: boolean }[];
  min?: number | string;
  max?: number | string;
  maxFiles?: number; // <-- Add this line
  // ...other props...
  data?: import("../LicensePlate/LicensePlate").LicensePlateData;
  value?:
    | string
    | number
    | boolean
    | null
    | undefined
    | import("../LicensePlate/LicensePlate").LicensePlateData;
  onChange?: (
    value: import("../LicensePlate/LicensePlate").LicensePlateData
  ) => void;
  plateColor?: import("../LicensePlate/LicensePlate").PlateColor;
  variant?: "mini" | "small" | "normal";
  readOnly?: boolean;
  hidden?: boolean;
  extraInfo?: string;
  theme?: import("../LicensePlate/LicensePlate").LicensePlateTheme;
  showRestrictionIcon?: boolean;
  showQRCode?: boolean;
  headerLabel?: string;
  footerStateLabel?: string;
  footerExtraLabel?: string;
  city?: string;
  fontSize?: number | string;
  onFocus?: React.FocusEventHandler<HTMLDivElement>;
  onBlur?: React.FocusEventHandler<HTMLDivElement>;
  children?: React.ReactNode;
  showLogo?: boolean;
  showFlag?: boolean;

  // Props específicas para input fields
  as?: "input" | "textarea" | "custom";
  name?: string;
  placeholder?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  onRightIconClick?: () => void;
  mask?: string | RegExp | string[];
  replacement?: Record<string, RegExp>;
  validationRegex?: RegExp;
  accessibilityLabel?: string;
  accessibilityHint?: string;
  accessibilityErrorMessage?: string;
  onError?: (errorMessage: string) => void;
  autoComplete?: string;
  autoFocus?: boolean;
  className?: string;
  inputClassName?: string;
  maxLength?: number;
  showCharCount?: boolean;
  customValidation?: (value: unknown) => string | undefined;
  inputProps?: React.InputHTMLAttributes<HTMLInputElement> &
    React.TextareaHTMLAttributes<HTMLTextAreaElement>;
  tabIndex?: number;
  i18nMessages?: unknown;
  containerProps?: React.HTMLAttributes<HTMLDivElement>;
  tooltip?: string;
  indeterminate?: boolean;
  // --- CodeField specific props ---
  codeFormats?: ("qr" | "barcode")[];
  barcodeTypes?: string[];
  codeMode?: "edit" | "view";
  codeSize?: number;
  codeDownloadButton?: boolean;
  codeElevation?: boolean;
  codePlaceholder?: string;
}
// ...existing code...

/**
 * Props para FormFields
 */
// Add this type definition above the FormField interface (for example, after FormField interface)
export type ExplicitFormField = FormField;

export interface FormFieldsProps<
  F extends ExplicitFormField = ExplicitFormField
> {
  fields: Array<Array<F & { id: string }>>;
  formData: Record<string, unknown>;
  mode: "create" | "read" | "update";
  theme?: "light" | "dark";
  handleFieldChange: (id: string, value: unknown) => void;
  handleFocus: (id: string) => void;
  handleBlur: (id: string, value: unknown) => void;
  fieldStatus: Record<string, { error?: string; success?: string }>;
  columns?: 1 | 2 | 3 | 4 | 5 | 6;
  className?: string;
  style?: React.CSSProperties;
  renderCustomField: (
    field: F,
    props: Record<string, unknown>
  ) => React.ReactNode | null | undefined;
  onValid?: (fieldId: string) => void;
  remoteValidationStatus?: Record<
    string,
    "idle" | "validating" | "success" | "error"
  >;
  showOptionalFields?: boolean;
  /**
   * Permite sobrescrever props de todos os campos dinamicamente.
   * Pode ser um objeto (aplicado a todos) ou uma função (por campo).
   */
  fieldPropsOverride?: Partial<F> | ((field: F) => Partial<F>);
  /**
   * Callback opcional para feedback global (ex: upload falhou).
   * O Form ou o Pai deve usar isso para Toast global, se desejar.
   */
  onFeedback?: (feedback: {
    message: string;
    type: "success" | "error" | "info";
    context?: string;
  }) => void;
}

const FormFields = <F extends ExplicitFormField = ExplicitFormField>({
  fields,
  formData,
  mode,
  theme = "light",
  handleFieldChange,
  handleFocus,
  handleBlur,
  fieldStatus,
  columns = 1,
  className,
  style,
  renderCustomField,
  onValid,
  remoteValidationStatus,
  showOptionalFields = true,
  fieldPropsOverride,
  onFeedback,
}: FormFieldsProps<F>) => {
  // NÃO memoize funções vindas das props!
  // Use-as diretamente:
  // handleFieldChange, handleFocus, handleBlur, onValid, onFeedback

  // Memoize handlers que recebem o id do campo
  const handleChange = useCallback(
    (id: string, value: unknown) => handleFieldChange(id, value),
    [handleFieldChange]
  );
  const handleBlurCb = useCallback(
    (id: string, value: unknown) => handleBlur(id, value),
    [handleBlur]
  );
  const handleFocusCb = useCallback(
    (id: string) => handleFocus(id),
    [handleFocus]
  );

  // Memoize upload handler por campo
  const getUploadHandler = useCallback(
    (field: F) =>
      (files: UploadFileWithPath[], error?: string | Error | null) => {
        if (error && onFeedback) {
          onFeedback({
            message: String(error),
            type: "error",
            context: "upload",
          });
        }
        const fileList = files.map((f) => f.file as File);
        handleFieldChange(
          field.id,
          typeof (field as F & { multiple?: boolean }).multiple === "boolean" &&
            (field as F & { multiple?: boolean }).multiple
            ? fileList
            : fileList[0] || null
        );
      },
    [onFeedback, handleFieldChange]
  );

  // Chama onValid para cada campo válido (sem erro) quando fieldStatus muda
  useEffect(() => {
    if (!onValid) return;
    fields.flat().forEach((field) => {
      const status = fieldStatus[field.id];
      if (status && !status.error) {
        onValid(field.id);
      }
    });
  }, [fields, fieldStatus, onValid]);

  // Ref para o primeiro campo visível
  const firstFieldRef = useRef<HTMLInputElement | null>(null);

  // Foca automaticamente no primeiro campo visível ao montar
  useEffect(() => {
    if (firstFieldRef.current) {
      firstFieldRef.current.focus();
    }
  }, []);

  // Apply field overrides (deep spread)
  const applyOverrides = useCallback(
    (field: F): F => {
      if (!fieldPropsOverride) return field;
      const overrides =
        typeof fieldPropsOverride === "function"
          ? fieldPropsOverride(field)
          : fieldPropsOverride;
      return { ...field, ...overrides };
    },
    [fieldPropsOverride]
  );

  // Status visual para validação remota
  const getRemoteStatusIcon = (field: F) => {
    const remoteStatus = remoteValidationStatus?.[field.id];
    if (remoteStatus === "validating")
      return (
        <span className="ml-2 text-xs text-blue-500 animate-pulse">
          Validando...
        </span>
      );
    if (remoteStatus === "success")
      return <span className="ml-2 text-xs text-green-600">✓</span>;
    if (remoteStatus === "error")
      return <span className="ml-2 text-xs text-red-500">✗</span>;
    return null;
  };

  // Adicione este hook antes da função renderField:
  const fieldHandlersRef = useRef<
    Record<
      string,
      {
        onChange: (value: unknown) => void;
        onBlur: (value: unknown) => void;
        onFocus: () => void;
      }
    >
  >({});

  const getFieldHandlers = useCallback(
    (fieldId: string) => {
      if (!fieldHandlersRef.current[fieldId]) {
        fieldHandlersRef.current[fieldId] = {
          onChange: (value: unknown) => {
            // Normaliza eventos (React.ChangeEvent / SyntheticEvent) para extrair o valor
            let nextValue = value;
            if (
              value &&
              typeof value === "object" &&
              "target" in (value as Record<string, unknown>) &&
              (value as { target?: unknown }).target
            ) {
              const tgt = (
                value as {
                  target: { value?: unknown; checked?: unknown; type?: string };
                }
              ).target;
              if (tgt) {
                if (tgt.type === "checkbox" || tgt.type === "radio") {
                  if ("checked" in tgt) nextValue = tgt.checked;
                } else if ("value" in tgt) {
                  nextValue = tgt.value;
                }
              }
            }
            handleChange(fieldId, nextValue);
          },
          onBlur: (value: unknown) => handleBlurCb(fieldId, value),
          onFocus: () => handleFocusCb(fieldId),
        };
      }
      return fieldHandlersRef.current[fieldId];
    },
    [handleChange, handleBlurCb, handleFocusCb]
  );

  // Prepare normalized options for all select fields at the top level using useMemo and async normalization
  const [selectFieldNormalizedOptions, setSelectFieldNormalizedOptions] =
    useState<
      Map<string, { label: string; value: string; disabled?: boolean }[]>
    >(new Map());

  useEffect(() => {
    let mounted = true;
    const normalizeAllOptions = async () => {
      const map = new Map<
        string,
        { label: string; value: string; disabled?: boolean }[]
      >();
      for (const field of fields.flat()) {
        if (field.type === "select") {
          const opts = await normalizeOptionsSafe(
            (
              field as F & {
                options?: {
                  label: string;
                  value: string;
                  disabled?: boolean;
                }[];
              }
            ).options,
            field,
            formData
          );
          // CLONE: transforma readonly em array mutável
          map.set(field.id, [...opts]);
        }
      }
      if (mounted) setSelectFieldNormalizedOptions(map);
    };
    normalizeAllOptions();
    return () => {
      mounted = false;
    };
  }, [fields, formData]);

  // Renderização de campo único
  const renderField = (field: F, isFirstField = false) => {
    const fieldWithOverrides = applyOverrides(field);
    const status = fieldStatus[fieldWithOverrides.id] || {};
    const isDisabled = mode === "read" || fieldWithOverrides.disabled;

    // Handlers por campo (não useCallback aqui)
    const { onChange, onBlur, onFocus } = getFieldHandlers(
      fieldWithOverrides.id
    );

    if (renderCustomField) {
      const custom = renderCustomField(fieldWithOverrides, {
        value: formData[fieldWithOverrides.id],
        onChange,
        onBlur,
        onFocus,
        disabled: isDisabled,
        mode,
        theme,
        field: fieldWithOverrides,
        error: status.error,
        success: status.success,
      });
      if (custom) return custom;
    }

    // Adapte cada campo conforme necessário
    switch (fieldWithOverrides.type) {
      case "input": {
        const inputFieldProps = fieldWithOverrides as F & {
          inputType?: string;
          as?: string;
        };
        // Substitui Set por array tipada para preservar literais (REMOVIDO "file")
        const allowedInputTypes = [
          "text",
          "number",
          "email",
          "password",
          "tel",
          "phone",
          "url",
          "search",
          "date",
          "time",
          "datetime",
          "datetime-local",
          "month",
          "week",
          "color",
          "checkbox",
          "radio",
          "range",
          "hidden",
          "cep",
          "money",
          "cpf",
        ] as const;
        type AllowedInputType = (typeof allowedInputTypes)[number];
        const resolveInputType = (raw?: string): AllowedInputType => {
          if (!raw) return "text";
          const lower = raw as string;
          return (allowedInputTypes as readonly string[]).includes(lower)
            ? (lower as AllowedInputType)
            : "text";
        };
        const safeInputType: AllowedInputType = resolveInputType(
          inputFieldProps.inputType
        );

        // allowedAs / safeAs (reintroduzido)
        const allowedAs = ["input", "textarea", "custom"] as const;
        type AllowedAs = (typeof allowedAs)[number];
        const safeAs: AllowedAs = allowedAs.includes(
          (inputFieldProps.as as AllowedAs) || "input"
        )
          ? (inputFieldProps.as as AllowedAs) || "input"
          : "input";

        // NOVO: valor bruto sem coerção para string; InputField já trata internamente
        const rawValue = formData[fieldWithOverrides.id];

        // NOVO: decide se deve autoFocus usando isFirstField
        const shouldAutoFocus =
          isFirstField &&
          !isDisabled &&
          typeof inputFieldProps.autoFocus !== "boolean";

        // NOVO: construir errorMessage apenas se não houver status.error (evita duplicação)
        const computedErrorMessage = status.error
          ? status.error
          : typeof inputFieldProps.errorMessage === "string"
          ? inputFieldProps.errorMessage
          : typeof inputFieldProps.errorMessage === "function"
          ? (inputFieldProps.errorMessage as (context: unknown) => string)
          : undefined;

        return (
          <InputField
            // Usa ref para permitir foco via efeito inicial
            ref={
              shouldAutoFocus
                ? (firstFieldRef as unknown as React.Ref<
                    HTMLInputElement | HTMLTextAreaElement
                  >)
                : undefined
            }
            id={fieldWithOverrides.id}
            name={
              typeof inputFieldProps.name === "string"
                ? inputFieldProps.name
                : fieldWithOverrides.id
            } // NOVO: garante atributo name
            placeholder={
              typeof inputFieldProps.placeholder === "string"
                ? inputFieldProps.placeholder
                : undefined
            }
            label={
              typeof inputFieldProps.label === "string"
                ? inputFieldProps.label
                : undefined
            }
            type={safeInputType}
            value={
              rawValue === undefined || rawValue === null
                ? ""
                : (rawValue as unknown) // delega parsing interno
            } // NOVO: passa valor cru
            error={!!status.error}
            errorMessage={computedErrorMessage} // NOVO
            disabled={!!isDisabled}
            helperText={
              typeof inputFieldProps.helperText === "string"
                ? inputFieldProps.helperText
                : undefined
            }
            required={!!inputFieldProps.required}
            theme={
              typeof inputFieldProps.theme === "string" &&
              (inputFieldProps.theme === "light" ||
                inputFieldProps.theme === "dark")
                ? inputFieldProps.theme
                : undefined
            }
            leftIcon={inputFieldProps.leftIcon as React.ReactNode}
            rightIcon={inputFieldProps.rightIcon as React.ReactNode}
            onRightIconClick={
              typeof inputFieldProps.onRightIconClick === "function" &&
              inputFieldProps.onRightIconClick.length === 0
                ? (inputFieldProps.onRightIconClick as () => void)
                : undefined
            }
            mask={
              typeof inputFieldProps.mask === "string" ||
              inputFieldProps.mask instanceof RegExp ||
              (Array.isArray(inputFieldProps.mask) &&
                inputFieldProps.mask.every((item) => typeof item === "string"))
                ? (inputFieldProps.mask as string | RegExp | string[])
                : undefined
            }
            replacement={
              typeof inputFieldProps.replacement === "object" &&
              inputFieldProps.replacement !== null &&
              !Array.isArray(inputFieldProps.replacement)
                ? (inputFieldProps.replacement as Record<string, RegExp>)
                : undefined
            }
            validationRegex={
              inputFieldProps.validationRegex instanceof RegExp
                ? inputFieldProps.validationRegex
                : undefined
            }
            accessibilityLabel={
              typeof inputFieldProps.accessibilityLabel === "string"
                ? inputFieldProps.accessibilityLabel
                : undefined
            }
            accessibilityHint={
              typeof inputFieldProps.accessibilityHint === "string"
                ? inputFieldProps.accessibilityHint
                : undefined
            }
            accessibilityErrorMessage={
              typeof inputFieldProps.accessibilityErrorMessage === "string"
                ? inputFieldProps.accessibilityErrorMessage
                : undefined
            }
            onFocus={onFocus}
            onBlur={() => onBlur(undefined)}
            onError={
              typeof inputFieldProps.onError === "function" &&
              inputFieldProps.onError.length === 1
                ? (inputFieldProps.onError as (errorMessage: string) => void)
                : undefined
            }
            autoComplete={
              typeof inputFieldProps.autoComplete === "string"
                ? inputFieldProps.autoComplete
                : undefined
            } // NOVO: propaga autoComplete
            autoFocus={
              typeof inputFieldProps.autoFocus === "boolean"
                ? inputFieldProps.autoFocus
                : shouldAutoFocus
            }
            successMessage={
              status.success ??
              (typeof inputFieldProps.successMessage === "string"
                ? inputFieldProps.successMessage
                : typeof inputFieldProps.successMessage === "function"
                ? (inputFieldProps.successMessage as (
                    context: unknown
                  ) => string)
                : undefined)
            }
            successIcon={inputFieldProps.successIcon as React.ReactNode}
            readOnly={
              typeof inputFieldProps.readOnly === "boolean"
                ? inputFieldProps.readOnly
                : undefined
            }
            className={
              typeof inputFieldProps.className === "string"
                ? inputFieldProps.className
                : undefined
            }
            inputClassName={
              typeof inputFieldProps.inputClassName === "string"
                ? inputFieldProps.inputClassName
                : undefined
            }
            as={safeAs}
            maxLength={
              typeof inputFieldProps.maxLength === "number"
                ? inputFieldProps.maxLength
                : undefined
            }
            showCharCount={
              typeof inputFieldProps.showCharCount === "boolean"
                ? inputFieldProps.showCharCount
                : undefined
            }
            customValidation={
              typeof inputFieldProps.customValidation === "function"
                ? (inputFieldProps.customValidation as (
                    value: unknown
                  ) => string | undefined)
                : undefined
            }
            inputProps={
              inputFieldProps.inputProps &&
              typeof inputFieldProps.inputProps === "object"
                ? (inputFieldProps.inputProps as React.InputHTMLAttributes<HTMLInputElement> &
                    React.TextareaHTMLAttributes<HTMLTextAreaElement>)
                : undefined
            }
            tabIndex={
              typeof inputFieldProps.tabIndex === "number"
                ? inputFieldProps.tabIndex
                : undefined
            }
            i18nMessages={
              inputFieldProps.i18nMessages &&
              typeof inputFieldProps.i18nMessages === "object"
                ? (inputFieldProps.i18nMessages as import("../InputField/InputField").InputFieldI18nContext)
                : undefined
            } // NOVO: propaga i18nMessages
            containerProps={
              inputFieldProps.containerProps &&
              typeof inputFieldProps.containerProps === "object"
                ? (inputFieldProps.containerProps as React.HTMLAttributes<HTMLDivElement>)
                : undefined
            }
            tooltip={
              typeof inputFieldProps.tooltip === "string"
                ? inputFieldProps.tooltip
                : undefined
            }
            data-testid={`input-${fieldWithOverrides.id}`} // NOVO: id previsível p/ testes
            onChange={onChange}
          >
            {inputFieldProps.children as React.ReactNode}
          </InputField>
        );
      }
      case "code": {
        // Valor esperado: { text: string, format: 'qr' | 'barcode', barcodeType?: string }
        // Ensure barcodeType is cast to BarcodeType | undefined
        type BarcodeType =
          | "CODE128"
          | "EAN13"
          | "EAN8"
          | "UPC"
          | "ITF"
          | "MSI"
          | "CODABAR";
        const rawValue = (formData[
          fieldWithOverrides.id
        ] as Partial<CodeFieldValue>) || { text: "", format: "qr" };
        const codeFieldValue: CodeFieldValue = {
          text: rawValue.text ?? "",
          format: rawValue.format ?? "qr",
          barcodeType: rawValue.barcodeType as BarcodeType | undefined,
        };
        const editMode = mode !== "read";
        return (
          <React.Suspense fallback={<div>Carregando...</div>}>
            <div
              key={fieldWithOverrides.id}
              className={clsx("flex flex-col", fieldWithOverrides.className)}
            >
              <CodeFieldComp
                label={fieldWithOverrides.label}
                mode={
                  fieldWithOverrides.codeMode || (editMode ? "edit" : "view")
                }
                value={codeFieldValue}
                onChange={onChange}
                formats={fieldWithOverrides.codeFormats}
                barcodeTypes={
                  fieldWithOverrides.barcodeTypes as BarcodeType[] | undefined
                }
                placeholder={fieldWithOverrides.codePlaceholder}
                disabled={isDisabled}
                helperText={fieldWithOverrides.helperText}
                required={fieldWithOverrides.required}
                size={fieldWithOverrides.codeSize}
                elevation={fieldWithOverrides.codeElevation}
                downloadButton={fieldWithOverrides.codeDownloadButton}
                error={!!status.error}
                errorMessage={status.error}
                successMessage={status.success}
                theme={theme}
              />
            </div>
          </React.Suspense>
        );
      }
      case "select": {
        const selectFieldProps = fieldWithOverrides as F & SelectFieldProps;

        // Use normalized options from the top-level map
        const normalizedOptions =
          selectFieldNormalizedOptions.get(fieldWithOverrides.id) ?? [];

        return (
          <SelectField
            id={fieldWithOverrides.id}
            options={
              normalizedOptions as import("../SelectField/SelectField").SelectOption[]
            }
            placeholder={
              typeof selectFieldProps.placeholder === "string"
                ? selectFieldProps.placeholder
                : undefined
            }
            label={
              typeof selectFieldProps.label === "string"
                ? selectFieldProps.label
                : undefined
            }
            error={!!status.error}
            errorMessage={
              status.error ??
              (typeof selectFieldProps.errorMessage === "string"
                ? selectFieldProps.errorMessage
                : typeof selectFieldProps.errorMessage === "function"
                ? (selectFieldProps.errorMessage as (ctx: {
                    value: string;
                  }) => string)
                : undefined)
            }
            disabled={!!isDisabled}
            helperText={
              typeof selectFieldProps.helperText === "string"
                ? selectFieldProps.helperText
                : undefined
            }
            required={!!selectFieldProps.required}
            theme={
              typeof selectFieldProps.theme === "string" &&
              (selectFieldProps.theme === "light" ||
                selectFieldProps.theme === "dark")
                ? selectFieldProps.theme
                : undefined
            }
            searchable={
              typeof selectFieldProps.searchable === "boolean"
                ? selectFieldProps.searchable
                : undefined
            }
            accessibilityLabel={
              typeof selectFieldProps.accessibilityLabel === "string"
                ? selectFieldProps.accessibilityLabel
                : undefined
            }
            accessibilityErrorMessage={
              typeof selectFieldProps.accessibilityErrorMessage === "string"
                ? selectFieldProps.accessibilityErrorMessage
                : undefined
            }
            successMessage={
              status.success ??
              (typeof selectFieldProps.successMessage === "string"
                ? selectFieldProps.successMessage
                : typeof selectFieldProps.successMessage === "function"
                ? (selectFieldProps.successMessage as (ctx: {
                    value: string;
                  }) => string)
                : undefined)
            }
            successIcon={selectFieldProps.successIcon as React.ReactNode}
            readOnly={
              typeof selectFieldProps.readOnly === "boolean"
                ? selectFieldProps.readOnly
                : undefined
            }
            className={
              typeof selectFieldProps.className === "string"
                ? selectFieldProps.className
                : undefined
            }
            inputClassName={
              typeof selectFieldProps.inputClassName === "string"
                ? selectFieldProps.inputClassName
                : undefined
            }
            containerProps={
              selectFieldProps.containerProps &&
              typeof selectFieldProps.containerProps === "object"
                ? (selectFieldProps.containerProps as React.HTMLAttributes<HTMLDivElement>)
                : undefined
            }
            tabIndex={
              typeof selectFieldProps.tabIndex === "number"
                ? selectFieldProps.tabIndex
                : undefined
            }
            i18nMessages={
              typeof selectFieldProps.i18nMessages === "object" &&
              selectFieldProps.i18nMessages !== null
                ? (selectFieldProps.i18nMessages as import("../SelectField/SelectField").SelectFieldI18nContext)
                : undefined
            }
            renderOption={
              typeof selectFieldProps.renderOption === "function"
                ? selectFieldProps.renderOption
                : undefined
            }
            multiple={
              typeof selectFieldProps.multiple === "boolean"
                ? selectFieldProps.multiple
                : undefined
            }
            value={
              Array.isArray(formData[fieldWithOverrides.id])
                ? (formData[fieldWithOverrides.id] as string[])
                : typeof formData[fieldWithOverrides.id] === "string"
                ? (formData[fieldWithOverrides.id] as string)
                : selectFieldProps.multiple
                ? []
                : ""
            }
            onChange={onChange}
            maxSelected={
              typeof selectFieldProps.maxSelected === "number"
                ? selectFieldProps.maxSelected
                : undefined
            }
            onFocus={onFocus}
            onBlur={() => onBlur(undefined)}
            searchInputRef={selectFieldProps.searchInputRef}
            dropdownFooter={selectFieldProps.dropdownFooter}
            dropdownClassName={
              typeof selectFieldProps.dropdownClassName === "string"
                ? selectFieldProps.dropdownClassName
                : undefined
            }
            optionClassName={
              typeof selectFieldProps.optionClassName === "string"
                ? selectFieldProps.optionClassName
                : undefined
            }
            searchInputClassName={
              typeof selectFieldProps.searchInputClassName === "string"
                ? selectFieldProps.searchInputClassName
                : undefined
            }
            tooltip={
              typeof selectFieldProps.tooltip === "string"
                ? selectFieldProps.tooltip
                : undefined
            }
          />
        );
      }
      case "toggle": {
        // Extend ToggleProps locally to include optional successTimeoutMs (not present in original type)
        type TogglePropsWithTimeout = ToggleProps & {
          successTimeoutMs?: number;
        };
        const toggleFieldProps = fieldWithOverrides as F &
          TogglePropsWithTimeout;

        // Merge containerProps to inject focus/blur handlers since Toggle does not expose onFocus/onBlur props
        const mergedContainerProps: React.HTMLAttributes<HTMLDivElement> = {
          ...(toggleFieldProps.containerProps &&
          typeof toggleFieldProps.containerProps === "object"
            ? (toggleFieldProps.containerProps as React.HTMLAttributes<HTMLDivElement>)
            : {}),
          onBlur: (e) => {
            if (
              typeof (
                toggleFieldProps.containerProps as
                  | React.HTMLAttributes<HTMLDivElement>
                  | undefined
              )?.onBlur === "function"
            ) {
              (
                toggleFieldProps.containerProps as React.HTMLAttributes<HTMLDivElement>
              ).onBlur!(e);
            }
            onBlur(!!formData[fieldWithOverrides.id]);
          },
          onFocus: (e) => {
            if (
              typeof (
                toggleFieldProps.containerProps as
                  | React.HTMLAttributes<HTMLDivElement>
                  | undefined
              )?.onFocus === "function"
            ) {
              (
                toggleFieldProps.containerProps as React.HTMLAttributes<HTMLDivElement>
              ).onFocus!(e);
            }
            onFocus();
          },
        };

        return (
          <Toggle
            id={fieldWithOverrides.id}
            value={!!formData[fieldWithOverrides.id]}
            onValueChange={onChange}
            label={
              typeof toggleFieldProps.label === "string"
                ? toggleFieldProps.label
                : undefined
            }
            disabled={!!isDisabled}
            readOnly={
              typeof toggleFieldProps.readOnly === "boolean"
                ? toggleFieldProps.readOnly
                : undefined
            }
            required={!!toggleFieldProps.required}
            theme={
              typeof toggleFieldProps.theme === "string" &&
              (toggleFieldProps.theme === "light" ||
                toggleFieldProps.theme === "dark")
                ? toggleFieldProps.theme
                : undefined
            }
            error={!!status.error}
            errorMessage={
              status.error ??
              (typeof toggleFieldProps.errorMessage === "string"
                ? toggleFieldProps.errorMessage
                : typeof toggleFieldProps.errorMessage === "function"
                ? (toggleFieldProps.errorMessage as (value: boolean) => string)
                : undefined)
            }
            successMessage={
              status.success ??
              (typeof toggleFieldProps.successMessage === "string"
                ? toggleFieldProps.successMessage
                : typeof toggleFieldProps.successMessage === "function"
                ? (toggleFieldProps.successMessage as (
                    value: boolean
                  ) => string)
                : undefined)
            }
            helperText={
              typeof toggleFieldProps.helperText === "string"
                ? toggleFieldProps.helperText
                : undefined
            }
            className={
              typeof toggleFieldProps.className === "string"
                ? toggleFieldProps.className
                : undefined
            }
            containerProps={mergedContainerProps}
            tabIndex={
              typeof toggleFieldProps.tabIndex === "number"
                ? toggleFieldProps.tabIndex
                : undefined
            }
            autoFocus={
              typeof toggleFieldProps.autoFocus === "boolean"
                ? toggleFieldProps.autoFocus
                : undefined
            }
            accessibilityLabel={
              typeof toggleFieldProps.accessibilityLabel === "string"
                ? toggleFieldProps.accessibilityLabel
                : undefined
            }
            accessibilityErrorMessage={
              typeof toggleFieldProps.accessibilityErrorMessage === "string"
                ? toggleFieldProps.accessibilityErrorMessage
                : undefined
            }
            i18nMessages={
              typeof toggleFieldProps.i18nMessages === "object" &&
              toggleFieldProps.i18nMessages !== null
                ? (toggleFieldProps.i18nMessages as import("../Toggle/Toggle").ToggleI18nContext)
                : undefined
            }
            data-testid={toggleFieldProps["data-testid"]}
          />
        );
      }
      case "radiobutton": {
        const radioButtonProps = fieldWithOverrides as F & RadioButtonProps;
        return (
          <RadioButton
            options={
              Array.isArray(radioButtonProps.options)
                ? radioButtonProps.options
                : []
            }
            selectedValue={
              typeof formData[fieldWithOverrides.id] === "string"
                ? (formData[fieldWithOverrides.id] as string)
                : ""
            }
            onValueChange={onChange}
            onBlur={onBlur}
            onFocus={onFocus}
            name={fieldWithOverrides.id}
            label={
              typeof radioButtonProps.label === "string"
                ? radioButtonProps.label
                : undefined
            }
            description={
              typeof radioButtonProps.description === "string"
                ? radioButtonProps.description
                : undefined
            }
            disabled={!!isDisabled}
            theme={
              typeof radioButtonProps.theme === "string" &&
              (radioButtonProps.theme === "light" ||
                radioButtonProps.theme === "dark")
                ? radioButtonProps.theme
                : undefined
            }
            required={!!radioButtonProps.required}
            helperText={
              typeof radioButtonProps.helperText === "string"
                ? radioButtonProps.helperText
                : undefined
            }
            error={!!status.error}
            errorMessage={
              status.error ??
              (typeof radioButtonProps.errorMessage === "string"
                ? radioButtonProps.errorMessage
                : undefined)
            }
            size={
              typeof radioButtonProps.size === "string"
                ? radioButtonProps.size
                : undefined
            }
            primaryColor={
              typeof radioButtonProps.primaryColor === "string"
                ? radioButtonProps.primaryColor
                : undefined
            }
            defaultValue={
              typeof radioButtonProps.defaultValue === "string"
                ? radioButtonProps.defaultValue
                : undefined
            }
            autoFocus={
              typeof radioButtonProps.autoFocus === "boolean"
                ? radioButtonProps.autoFocus
                : undefined
            }
            inputProps={
              typeof radioButtonProps.inputProps === "object"
                ? radioButtonProps.inputProps
                : undefined
            }
            data-testid={radioButtonProps["data-testid"]}
          />
        );
      }
      case "upload": {
        const uploadFieldProps = fieldWithOverrides as F & UploadProps;
        return (
          <Upload
            onFilesSelected={getUploadHandler(fieldWithOverrides)}
            accept={uploadFieldProps.accept}
            maxFiles={
              typeof uploadFieldProps.maxFiles === "number"
                ? uploadFieldProps.maxFiles
                : undefined
            }
            multiple={
              typeof uploadFieldProps.multiple === "boolean"
                ? uploadFieldProps.multiple
                : undefined
            }
            label={
              typeof uploadFieldProps.label === "string"
                ? uploadFieldProps.label
                : undefined
            }
            disabled={!!isDisabled}
            buttonTitle={
              typeof uploadFieldProps.buttonTitle === "string"
                ? uploadFieldProps.buttonTitle
                : undefined
            }
            autoUpload={
              typeof uploadFieldProps.autoUpload === "boolean"
                ? uploadFieldProps.autoUpload
                : undefined
            }
            onUpload={
              typeof uploadFieldProps.onUpload === "function"
                ? uploadFieldProps.onUpload
                : undefined
            }
            convertToWebP={
              typeof uploadFieldProps.convertToWebP === "boolean"
                ? uploadFieldProps.convertToWebP
                : undefined
            }
            onProcessFile={
              typeof uploadFieldProps.onProcessFile === "function"
                ? uploadFieldProps.onProcessFile
                : undefined
            }
            tooltip={
              typeof uploadFieldProps.tooltip === "string"
                ? uploadFieldProps.tooltip
                : undefined
            }
            helperText={
              typeof uploadFieldProps.helperText === "string"
                ? uploadFieldProps.helperText
                : undefined
            }
            errorMessage={
              status.error ??
              (typeof uploadFieldProps.errorMessage === "string"
                ? uploadFieldProps.errorMessage
                : typeof uploadFieldProps.errorMessage === "function"
                ? (uploadFieldProps.errorMessage as (files: File[]) => string)
                : undefined)
            }
            successMessage={
              status.success ??
              (typeof uploadFieldProps.successMessage === "string"
                ? uploadFieldProps.successMessage
                : typeof uploadFieldProps.successMessage === "function"
                ? (uploadFieldProps.successMessage as (files: File[]) => string)
                : undefined)
            }
            i18nMessages={
              typeof uploadFieldProps.i18nMessages === "object" &&
              uploadFieldProps.i18nMessages !== null
                ? uploadFieldProps.i18nMessages
                : undefined
            }
            containerProps={
              uploadFieldProps.containerProps &&
              typeof uploadFieldProps.containerProps === "object"
                ? uploadFieldProps.containerProps
                : undefined
            }
            inputProps={
              uploadFieldProps.inputProps &&
              typeof uploadFieldProps.inputProps === "object"
                ? uploadFieldProps.inputProps
                : undefined
            }
            customValidation={
              typeof uploadFieldProps.customValidation === "function"
                ? uploadFieldProps.customValidation
                : undefined
            }
            maxFileSize={
              typeof uploadFieldProps.maxFileSize === "number"
                ? uploadFieldProps.maxFileSize
                : undefined
            }
            loadingMessage={
              typeof uploadFieldProps.loadingMessage === "string"
                ? uploadFieldProps.loadingMessage
                : undefined
            }
            theme={
              typeof uploadFieldProps.theme === "string"
                ? uploadFieldProps.theme
                : undefined
            }
            data-testid={uploadFieldProps["data-testid"]}
            directory={
              typeof uploadFieldProps.directory === "boolean"
                ? uploadFieldProps.directory
                : undefined
            }
            SuccessIcon={uploadFieldProps.SuccessIcon}
            ErrorIcon={uploadFieldProps.ErrorIcon}
            RemoveIcon={uploadFieldProps.RemoveIcon}
            InfoIcon={uploadFieldProps.InfoIcon}
            selectedFiles={uploadFieldProps.selectedFiles}
            components={uploadFieldProps.components}
            classNames={uploadFieldProps.classNames}
          />
        );
      }
      case "date":
      case "time":
      case "datetime": {
        const datetimepickerProps = fieldWithOverrides as F &
          DatetimepickerProps;
        return (
          <Datetimepicker
            id={fieldWithOverrides.id}
            value={
              typeof formData[fieldWithOverrides.id] === "string" ||
              formData[fieldWithOverrides.id] instanceof Date ||
              formData[fieldWithOverrides.id] === null ||
              formData[fieldWithOverrides.id] === undefined
                ? (formData[fieldWithOverrides.id] as string | Date | null)
                : ""
            }
            onChange={onChange}
            onBlur={onBlur}
            onFocus={onFocus}
            disabled={isDisabled}
            error={!!status.error}
            errorMessage={status.error ?? datetimepickerProps.errorMessage}
            mode={
              typeof datetimepickerProps.mode === "string"
                ? datetimepickerProps.mode
                : fieldWithOverrides.type
            }
            minDate={datetimepickerProps.minDate}
            maxDate={datetimepickerProps.maxDate}
            locale={
              typeof datetimepickerProps.locale === "string"
                ? datetimepickerProps.locale
                : undefined
            }
            datetimepickerI18nMessages={
              typeof datetimepickerProps.datetimepickerI18nMessages === "object"
                ? datetimepickerProps.datetimepickerI18nMessages
                : undefined
            }
            emitISO={
              typeof datetimepickerProps.emitISO === "boolean"
                ? datetimepickerProps.emitISO
                : undefined
            }
            customFormats={
              typeof datetimepickerProps.customFormats === "object"
                ? datetimepickerProps.customFormats
                : undefined
            }
            customParseFormats={
              typeof datetimepickerProps.customParseFormats === "object"
                ? datetimepickerProps.customParseFormats
                : undefined
            }
            customMasks={
              typeof datetimepickerProps.customMasks === "object"
                ? datetimepickerProps.customMasks
                : undefined
            }
            placeholder={
              typeof datetimepickerProps.placeholder === "string"
                ? datetimepickerProps.placeholder
                : undefined
            }
            label={
              typeof datetimepickerProps.label === "string"
                ? datetimepickerProps.label
                : undefined
            }
            required={!!datetimepickerProps.required}
            helperText={
              typeof datetimepickerProps.helperText === "string"
                ? datetimepickerProps.helperText
                : undefined
            }
            successMessage={
              typeof datetimepickerProps.successMessage === "string"
                ? datetimepickerProps.successMessage
                : undefined
            }
            successIcon={datetimepickerProps.successIcon as React.ReactNode}
            className={
              typeof datetimepickerProps.className === "string"
                ? datetimepickerProps.className
                : undefined
            }
            inputClassName={
              typeof datetimepickerProps.inputClassName === "string"
                ? datetimepickerProps.inputClassName
                : undefined
            }
            inputProps={
              typeof datetimepickerProps.inputProps === "object"
                ? datetimepickerProps.inputProps
                : undefined
            }
            containerProps={
              typeof datetimepickerProps.containerProps === "object"
                ? datetimepickerProps.containerProps
                : undefined
            }
            tooltip={
              typeof datetimepickerProps.tooltip === "string"
                ? datetimepickerProps.tooltip
                : undefined
            }
            tabIndex={
              typeof datetimepickerProps.tabIndex === "number"
                ? datetimepickerProps.tabIndex
                : undefined
            }
            autoFocus={
              typeof datetimepickerProps.autoFocus === "boolean"
                ? datetimepickerProps.autoFocus
                : undefined
            }
            accessibilityLabel={
              typeof datetimepickerProps.accessibilityLabel === "string"
                ? datetimepickerProps.accessibilityLabel
                : undefined
            }
            accessibilityErrorMessage={
              typeof datetimepickerProps.accessibilityErrorMessage === "string"
                ? datetimepickerProps.accessibilityErrorMessage
                : undefined
            }
          />
        );
      }
      case "checkbox": {
        const checkboxProps = fieldWithOverrides as F & CheckboxProps;
        return (
          <Checkbox
            id={fieldWithOverrides.id}
            checked={!!formData[fieldWithOverrides.id]}
            onChange={onChange}
            onBlur={onBlur}
            onFocus={onFocus}
            disabled={isDisabled}
            error={!!status.error}
            errorMessage={
              status.error ??
              (typeof checkboxProps.errorMessage === "string"
                ? checkboxProps.errorMessage
                : typeof checkboxProps.errorMessage === "function"
                ? (checkboxProps.errorMessage as (checked: boolean) => string)
                : undefined)
            }
            label={
              typeof checkboxProps.label === "string"
                ? checkboxProps.label
                : undefined
            }
            readOnly={
              typeof checkboxProps.readOnly === "boolean"
                ? checkboxProps.readOnly
                : undefined
            }
            required={!!checkboxProps.required}
            theme={
              typeof checkboxProps.theme === "string" &&
              (checkboxProps.theme === "light" ||
                checkboxProps.theme === "dark")
                ? checkboxProps.theme
                : undefined
            }
            successMessage={
              typeof checkboxProps.successMessage === "string"
                ? checkboxProps.successMessage
                : typeof checkboxProps.successMessage === "function"
                ? (checkboxProps.successMessage as (checked: boolean) => string)
                : undefined
            }
            helperText={
              typeof checkboxProps.helperText === "string"
                ? checkboxProps.helperText
                : undefined
            }
            leftIcon={checkboxProps.leftIcon as React.ReactNode}
            rightIcon={checkboxProps.rightIcon as React.ReactNode}
            onRightIconClick={
              typeof checkboxProps.onRightIconClick === "function"
                ? checkboxProps.onRightIconClick
                : undefined
            }
            tooltip={
              typeof checkboxProps.tooltip === "string"
                ? checkboxProps.tooltip
                : undefined
            }
            className={
              typeof checkboxProps.className === "string"
                ? checkboxProps.className
                : undefined
            }
            containerProps={
              checkboxProps.containerProps &&
              typeof checkboxProps.containerProps === "object"
                ? (checkboxProps.containerProps as React.HTMLAttributes<HTMLDivElement>)
                : undefined
            }
            tabIndex={
              typeof checkboxProps.tabIndex === "number"
                ? checkboxProps.tabIndex
                : undefined
            }
            autoFocus={
              typeof checkboxProps.autoFocus === "boolean"
                ? checkboxProps.autoFocus
                : undefined
            }
            accessibilityLabel={
              typeof checkboxProps.accessibilityLabel === "string"
                ? checkboxProps.accessibilityLabel
                : undefined
            }
            accessibilityErrorMessage={
              typeof checkboxProps.accessibilityErrorMessage === "string"
                ? checkboxProps.accessibilityErrorMessage
                : undefined
            }
            i18nMessages={
              typeof checkboxProps.i18nMessages === "object" &&
              checkboxProps.i18nMessages !== null
                ? checkboxProps.i18nMessages
                : undefined
            }
            indeterminate={
              typeof checkboxProps.indeterminate === "boolean"
                ? checkboxProps.indeterminate
                : undefined
            }
            successTimeoutMs={
              typeof checkboxProps.successTimeoutMs === "number"
                ? checkboxProps.successTimeoutMs
                : undefined
            }
            data-testid={checkboxProps["data-testid"]}
          >
            {checkboxProps.children as React.ReactNode}
          </Checkbox>
        );
      }
      case "smartimage": {
        const smartImageProps = fieldWithOverrides as F & SmartImageProps;
        return (
          <SmartImage
            id={smartImageProps.id}
            src={
              typeof smartImageProps.src === "string"
                ? smartImageProps.src
                : (formData[smartImageProps.id] as string) || ""
            }
            alt={
              typeof smartImageProps.alt === "string"
                ? smartImageProps.alt
                : typeof smartImageProps.label === "string"
                ? smartImageProps.label
                : ""
            }
            width={smartImageProps.width}
            height={smartImageProps.height}
            placeholderSrc={smartImageProps.placeholderSrc}
            style={smartImageProps.style}
            className={smartImageProps.className}
            frameClassName={smartImageProps.frameClassName}
            showZoomButton={
              typeof smartImageProps.showZoomButton === "boolean"
                ? smartImageProps.showZoomButton
                : undefined
            }
            disableZoom={
              typeof smartImageProps.disableZoom === "boolean"
                ? smartImageProps.disableZoom
                : undefined
            }
            tooltip={smartImageProps.tooltip}
            onLoadComplete={
              typeof smartImageProps.onLoadComplete === "function"
                ? smartImageProps.onLoadComplete
                : undefined
            }
            onError={
              typeof smartImageProps.onError === "function"
                ? smartImageProps.onError
                : undefined
            }
            customZoomContent={smartImageProps.customZoomContent}
            theme={smartImageProps.theme}
            responsiveSrcs={smartImageProps.responsiveSrcs}
            sources={smartImageProps.sources}
            priority={
              typeof smartImageProps.priority === "boolean"
                ? smartImageProps.priority
                : undefined
            }
            placeholder={smartImageProps.placeholder}
            blurDataURL={smartImageProps.blurDataURL}
            layout={smartImageProps.layout}
            quality={smartImageProps.quality}
            onLoadingProgress={
              typeof smartImageProps.onLoadingProgress === "function"
                ? smartImageProps.onLoadingProgress
                : undefined
            }
            errorFallback={smartImageProps.errorFallback}
            asBackground={
              typeof smartImageProps.asBackground === "boolean"
                ? smartImageProps.asBackground
                : undefined
            }
            fetchPriority={smartImageProps.fetchPriority}
            ariaHidden={
              typeof smartImageProps.ariaHidden === "boolean"
                ? smartImageProps.ariaHidden
                : undefined
            }
            // Passe outras props específicas se necessário
          />
        );
      }
      case "wysiwyg": {
        const wysiwygProps = fieldWithOverrides as F & InputWysiwygProps;
        return (
          <InputWysiwyg
            id={wysiwygProps.id}
            value={
              typeof formData[wysiwygProps.id] === "string"
                ? (formData[wysiwygProps.id] as string)
                : ""
            }
            onChange={onChange}
            onBlur={onBlur}
            onFocus={onFocus}
            disabled={isDisabled}
            error={!!status.error}
            errorMessage={status.error ?? wysiwygProps.errorMessage}
            label={
              typeof wysiwygProps.label === "string"
                ? wysiwygProps.label
                : undefined
            }
            helperText={
              typeof wysiwygProps.helperText === "string"
                ? wysiwygProps.helperText
                : undefined
            }
            required={!!wysiwygProps.required}
            placeholder={
              typeof wysiwygProps.placeholder === "string"
                ? wysiwygProps.placeholder
                : undefined
            }
            className={
              typeof wysiwygProps.className === "string"
                ? wysiwygProps.className
                : undefined
            }
            theme={
              typeof wysiwygProps.theme === "string" &&
              (wysiwygProps.theme === "light" || wysiwygProps.theme === "dark")
                ? wysiwygProps.theme
                : undefined
            }
            customValidation={
              typeof wysiwygProps.customValidation === "function"
                ? wysiwygProps.customValidation
                : undefined
            }
            autoFocus={
              typeof wysiwygProps.autoFocus === "boolean"
                ? wysiwygProps.autoFocus
                : undefined
            }
            maxLength={
              typeof wysiwygProps.maxLength === "number"
                ? wysiwygProps.maxLength
                : undefined
            }
            variables={
              Array.isArray(wysiwygProps.variables)
                ? wysiwygProps.variables
                : undefined
            }
            exportPdf={
              typeof wysiwygProps.exportPdf === "boolean"
                ? wysiwygProps.exportPdf
                : undefined
            }
            enableVariables={
              typeof wysiwygProps.enableVariables === "boolean"
                ? wysiwygProps.enableVariables
                : undefined
            }
          />
        );
      }
      case "colorpicker": {
        const colorPickerProps = fieldWithOverrides as F &
          import("../ColorPicker/ColorPicker").ColorPickerProps;
        return (
          <ColorPicker
            id={fieldWithOverrides.id}
            value={
              typeof formData[fieldWithOverrides.id] === "string"
                ? (formData[fieldWithOverrides.id] as string)
                : ""
            }
            onChange={onChange}
            onBlur={() => onBlur(undefined)} // <-- Fix: wrap to match () => void
            onFocus={onFocus}
            label={
              typeof colorPickerProps.label === "string"
                ? colorPickerProps.label
                : undefined
            }
            disabled={!!isDisabled}
            required={!!colorPickerProps.required}
            helperText={
              typeof colorPickerProps.helperText === "string"
                ? colorPickerProps.helperText
                : undefined
            }
            error={!!status.error}
            errorMessage={
              status.error ??
              (typeof colorPickerProps.errorMessage === "string"
                ? colorPickerProps.errorMessage
                : undefined)
            }
            theme={
              typeof colorPickerProps.theme === "string" &&
              (colorPickerProps.theme === "light" ||
                colorPickerProps.theme === "dark")
                ? colorPickerProps.theme
                : undefined
            }
            className={
              typeof colorPickerProps.className === "string"
                ? colorPickerProps.className
                : undefined
            }
            inputClassName={
              typeof colorPickerProps.inputClassName === "string"
                ? colorPickerProps.inputClassName
                : undefined
            }
            showPreview={
              typeof colorPickerProps.showPreview === "boolean"
                ? colorPickerProps.showPreview
                : undefined
            }
            palette={
              Array.isArray(colorPickerProps.palette)
                ? colorPickerProps.palette
                : undefined
            }
            paletteGroups={
              Array.isArray(colorPickerProps.paletteGroups)
                ? colorPickerProps.paletteGroups
                : undefined
            }
            autoFocus={
              typeof colorPickerProps.autoFocus === "boolean"
                ? colorPickerProps.autoFocus
                : undefined
            }
            tabIndex={
              typeof colorPickerProps.tabIndex === "number"
                ? colorPickerProps.tabIndex
                : undefined
            }
            showInputField={
              typeof colorPickerProps.showInputField === "boolean"
                ? colorPickerProps.showInputField
                : undefined
            }
            showCopy={
              typeof colorPickerProps.showCopy === "boolean"
                ? colorPickerProps.showCopy
                : undefined
            }
            showReset={
              typeof colorPickerProps.showReset === "boolean"
                ? colorPickerProps.showReset
                : undefined
            }
            defaultValue={
              typeof colorPickerProps.defaultValue === "string"
                ? colorPickerProps.defaultValue
                : undefined
            }
            advanced={
              typeof colorPickerProps.advanced === "boolean"
                ? colorPickerProps.advanced
                : undefined
            }
            previewSize={
              typeof colorPickerProps.previewSize === "number"
                ? colorPickerProps.previewSize
                : undefined
            }
            borderColor={
              typeof colorPickerProps.borderColor === "string"
                ? colorPickerProps.borderColor
                : undefined
            }
            borderColorFocused={
              typeof colorPickerProps.borderColorFocused === "string"
                ? colorPickerProps.borderColorFocused
                : undefined
            }
            borderColorError={
              typeof colorPickerProps.borderColorError === "string"
                ? colorPickerProps.borderColorError
                : undefined
            }
            borderColorSelected={
              typeof colorPickerProps.borderColorSelected === "string"
                ? colorPickerProps.borderColorSelected
                : undefined
            }
            paletteButtonSize={
              typeof colorPickerProps.paletteButtonSize === "number"
                ? colorPickerProps.paletteButtonSize
                : undefined
            }
            i18n={
              typeof colorPickerProps.i18n === "object"
                ? colorPickerProps.i18n
                : undefined
            }
            showNoneOption={
              typeof colorPickerProps.showNoneOption === "boolean"
                ? colorPickerProps.showNoneOption
                : undefined
            }
          />
        );
      }
      case "licenseplate":
        return (
          <LicensePlateInput
            value={formData[fieldWithOverrides.id] as string}
            onChange={onChange}
            // ...outras props
          />
        );
      default:
        return null;
    }
  };

  // Filtra campos: agora visibleIf tem precedência sobre a opção de ocultar opcionais.
  const filteredFields = useMemo(
    () =>
      fields.map((row) =>
        row.filter((field) => {
          if (field.hidden === true) return false;
          const visibleIfResult = field.visibleIf
            ? field.visibleIf(formData)
            : true;
          // Exibe se:
          // 1. visibleIfResult é true (sempre mostra, mesmo se opcional escondido)
          // 2. OU campo é obrigatório
          // 3. OU toggle para opcionais está ligado
          if (!visibleIfResult) return false;
          if (field.required) return true;
          if (showOptionalFields) return true;
          // Campo opcional, toggle off, mais visibleIfResult true => mostra
          if (field.visibleIf) return true;
          return false;
        })
      ),
    [fields, showOptionalFields, formData]
  );

  // Renderização em grid ou linhas
  if (columns && columns > 0) {
    let firstFieldRendered = false;
    return (
      <div className={clsx("flex flex-col", className)} style={style}>
        <div
          className={clsx(
            "grid gap-x-2 w-full",
            {
              1: "grid-cols-1",
              2: "md:grid-cols-2",
              3: "lg:grid-cols-3",
              4: "xl:grid-cols-4",
              5: "2xl:grid-cols-5",
              6: "2xl:grid-cols-6",
            }[columns] || `grid-cols-${columns}`
          )}
        >
          {filteredFields.flat().map((field: F) => {
            const isFirstField = !firstFieldRendered && field.type === "input";
            if (isFirstField) firstFieldRendered = true;
            return (
              <div
                key={field.id}
                className={clsx(
                  "min-w-0 w-full",
                  field.colSpan ? `col-span-${field.colSpan}` : "col-span-1",
                  field.rowSpan ? `row-span-${field.rowSpan}` : "",
                  field.type === "checkbox" && "flex items-center" // <-- centraliza verticalmente checkboxes
                )}
              >
                {renderField(field, isFirstField)}
                {getRemoteStatusIcon(field)}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // Render padrão: linhas
  return (
    <div className={clsx("flex flex-col", className)} style={style}>
      {filteredFields.map((row, rowIndex) => (
        <div
          key={rowIndex}
          className={clsx(
            "grid w-full gap-x-2",
            // Removido: "items-center",
            `grid-cols-${row.length > 0 ? row.length : 1}`
          )}
        >
          {row.map((field: F, idx) => {
            const isFirstField =
              rowIndex === 0 && idx === 0 && field.type === "input";
            return (
              <div
                key={field.id}
                className={clsx(
                  "min-w-0 w-full",
                  field.colSpan ? `col-span-${field.colSpan}` : "col-span-1",
                  field.rowSpan ? `row-span-${field.rowSpan}` : ""
                )}
              >
                {renderField(field, isFirstField)}
                {getRemoteStatusIcon(field)}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
};

const CodeFieldComp = React.lazy(() => import("../CodeField/CodeField"));

export default React.memo(FormFields);
